import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Color } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box } from 'native-base';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { ResponsiveSuccessIconButton } from '../../../../../../../../../components/ResponsiveSuccessIconButton';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import { TableContentItemText } from '../../../../../../../../../components/TableContent';
import { useCoesAssetController } from '../../../../../hooks/products/Coes';
import { useCoesBillingTypeController } from '../../../../../hooks/products/Coes/billing';
import { TextSkeleton } from '../../../../AssetsLoading';
import { AssetTitleWithSubtitle } from '../../../components/AssetTitleWithSubtitle';
import { LateralBar } from '../../../components/LateralBar';

import { CoesItemSelectedTypes } from '.';

export const CoesItemSelectedTablet: React.FC<CoesItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    currentValueFormatted,
    currentValue,
  } = useCoesAssetController(offer);

  const {
    billingTypeSelected,
    onChangeBillingTypeSelected,
    billingTypeOptions,
    applicationIn,
    isLoading,
  } = useCoesBillingTypeController({ product: offer });

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: currentValue || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.billingTypeId === billingTypeSelected || !billingTypeSelected) {
      return;
    }
    const updatedOffer = { ...offer, billingTypeId: billingTypeSelected };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingTypeSelected]);

  return (
    <Box p="inline.xxs">
      <LateralBar
        backgroundColor={Color['product.estruturados']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack flex={1} space="stack.xxxs">
        <HStack flex={1} justifyContent="space-between">
          <Stack flex={1} w="1/4">
            <AssetTitleWithSubtitle
              title={offer.productNameNormalized}
              subtitle={offer.strategyNormalized}
            />
          </Stack>
          <UI.ListItem testId="selected-product-risk" title="Risco" stackProps={{ w: '1/4' }}>
            <RiskRating
              currentRiskRating={offer.riskRatingScoreNormalized}
              tooltip={offer.riskRatingNormalized}
            />
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-maturityDate"
            title="Vencimento"
            stackProps={{ w: '1/4' }}>
            <TableContentItemText>{offer.maturityDateNormalized}</TableContentItemText>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-application-in"
            title="Aplicação em"
            stackProps={{ w: '1/4' }}>
            {isLoading ? (
              <TextSkeleton w="80%" />
            ) : (
              <TableContentItemText>{applicationIn || offer.applicationIn}</TableContentItemText>
            )}
          </UI.ListItem>
        </HStack>
        <HStack flex={1} justifyContent="space-between" alignItems="center">
          <UI.ListItem
            testId="selected-product-application-minimum"
            title="Aplicação mínima"
            stackProps={{ w: '1/4' }}>
            <TableContentItemText>
              {Utils.Mask.Money(offer.minimumApplicationNormalized)}
            </TableContentItemText>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-type-of-billing"
            title="Tipo de boletagem"
            stackProps={{ w: '1/4' }}>
            <Stack pr="inline.nano" w="full">
              <UI.Select
                testID="select-billing-option"
                triggerProps={{
                  isDisabled: isLoading || billingTypeOptions.length === 0,
                  _disabled: { opacity: 0.2 },
                }}
                options={billingTypeOptions}
                onValueChange={onChangeBillingTypeSelected}
                selectedValue={billingTypeSelected}
                placeholder="Selecione"
              />
            </Stack>
          </UI.ListItem>
          <HStack flex={1} flexGrow={1} space="inline.xxxs" alignItems="flex-end">
            <UI.ListItem
              testId="selected-product-name"
              title="Valor aplicado"
              stackProps={{ flexGrow: 1 }}>
              <Stack flexGrow={1} w="full">
                <InputMoney
                  id={`coes-input${id}`}
                  name="createAppliedAmount"
                  control={control}
                  handleFocus={handleInputFocus}
                  handleBlur={handleInputBlur}
                  handleSubmit={handleSubmit}
                  handleClear={handleInputClear}
                  currentValue={currentValueFormatted}
                  hasFocus={isFocused}
                  minValue={offer.minimumApplication}
                  customInputProps={{
                    ...{ ...(isFocused && hasError && { error: hasError }) },
                    ...{ ...(isFocused && !hasError && { helpText }) },
                  }}
                />
              </Stack>
            </UI.ListItem>
            <ResponsiveSuccessIconButton />
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
};
